import { formatDate, formatMoney } from "@/lib/format";
import {
  INVOICE_KIND_LABELS,
  INVOICE_PRINT_STYLES,
  SETTLEMENT_TYPE_LABELS,
  invoiceSheetStyle,
  resolveInvoicePageSize,
  type InvoiceRow,
  type Locale,
  type OrderItemRow,
  type OrderRow,
  type WorkspaceRow,
  type WorkspaceSettingsRow,
} from "@bizy/core";
import { getDictionary } from "@bizy/core/i18n/resolve";
import { forwardRef } from "react";

export interface InvoiceDocumentProps {
  invoice: InvoiceRow;
  order: Pick<OrderRow, "code" | "customer_name" | "customer_phone" | "settlement_type"> | null;
  items: readonly OrderItemRow[];
  workspace: WorkspaceRow;
  settings: WorkspaceSettingsRow;
  locale: Locale;
}

/**
 * The printable invoice sheet. Its geometry comes entirely from the selected
 * page size (`INVOICE_PAGE_SIZES`), so A4 / Letter / 80mm thermal all render from
 * this one component — screen preview and PNG raster share the same DOM.
 */
export const InvoiceDocument = forwardRef<HTMLDivElement, InvoiceDocumentProps>(
  ({ invoice, order, items, workspace, settings, locale }, ref) => {
    const dictionary = getDictionary(locale);
    const pageSize = resolveInvoicePageSize(invoice.page_size);
    const style = invoiceSheetStyle(pageSize);

    return (
      <div ref={ref} className="invoice-sheet" style={style}>
        <header className="flex items-start justify-between gap-4 border-b border-black/10 pb-3">
          <div>
            <p style={{ fontSize: `${INVOICE_PRINT_STYLES.headerFontPt}pt` }} className="font-semibold">
              {workspace.name}
            </p>
            {settings.address ? <p className="text-[9pt] opacity-80">{settings.address}</p> : null}
            {settings.whatsapp_number ?? settings.phone ? (
              <p className="text-[9pt] opacity-80">{settings.whatsapp_number ?? settings.phone}</p>
            ) : null}
          </div>
          <div className="text-right">
            <p className="text-[9pt] uppercase tracking-wide opacity-70">{dictionary.invoice.title}</p>
            <p className="font-semibold">{invoice.number}</p>
            <p className="text-[9pt] opacity-80">{INVOICE_KIND_LABELS[invoice.kind]}</p>
          </div>
        </header>

        <section className="mt-3 grid grid-cols-2 gap-3 text-[9pt]">
          <div>
            <p className="uppercase tracking-wide opacity-70">{dictionary.invoice.billTo}</p>
            <p className="font-medium">{order?.customer_name ?? "—"}</p>
            {order?.customer_phone ? <p className="opacity-80">{order.customer_phone}</p> : null}
          </div>
          <div className="text-right">
            <p>
              {dictionary.invoice.issuedAt}: {formatDate(invoice.issued_at)}
            </p>
            {invoice.due_at ? (
              <p>
                {dictionary.invoice.dueAt}: {formatDate(invoice.due_at)}
              </p>
            ) : null}
            {order ? (
              <p>
                {dictionary.invoice.settlementType}:{" "}
                {SETTLEMENT_TYPE_LABELS[order.settlement_type]}
              </p>
            ) : null}
          </div>
        </section>

        {items.length > 0 && !pageSize.isReceipt ? (
          <table className="mt-4 text-[9pt]">
            <thead>
              <tr className="border-b border-black/20 text-left">
                <th className="py-1">#</th>
                <th className="py-1">{dictionary.invoice.title}</th>
                <th className="py-1 text-right">{dictionary.invoice.qty}</th>
                <th className="py-1 text-right">{dictionary.invoice.price}</th>
                <th className="py-1 text-right">{dictionary.invoice.amount}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id} className="border-b border-black/5">
                  <td className="py-1">{index + 1}</td>
                  <td className="py-1">
                    {item.name_snapshot}
                    {item.variant_snapshot ? ` · ${item.variant_snapshot}` : ""}
                  </td>
                  <td className="py-1 text-right">{item.quantity}</td>
                  <td className="py-1 text-right">{formatMoney(item.unit_price, invoice.currency, locale)}</td>
                  <td className="py-1 text-right">{formatMoney(item.line_total, invoice.currency, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        <section className="mt-4 ml-auto w-full max-w-[60%] space-y-1 text-[9pt]">
          <div className="flex justify-between">
            <span className="opacity-70">{dictionary.invoice.total}</span>
            <span>{formatMoney(invoice.total_amount, invoice.currency, locale)}</span>
          </div>
          <div className="flex justify-between">
            <span className="opacity-70">{dictionary.invoice.paid}</span>
            <span>{formatMoney(invoice.paid_amount, invoice.currency, locale)}</span>
          </div>
          <div className="flex justify-between border-t border-black/20 pt-1 font-semibold">
            <span>{dictionary.invoice.balance}</span>
            <span>{formatMoney(invoice.balance_amount, invoice.currency, locale)}</span>
          </div>
        </section>

        <footer className="mt-6 border-t border-black/10 pt-2 text-[8pt] opacity-70">
          <p>
            {invoice.number} · {formatDate(invoice.issued_at)} · {pageSize.label}
          </p>
          <p>Generated by bizy</p>
        </footer>
      </div>
    );
  },
);

InvoiceDocument.displayName = "InvoiceDocument";
